// Lógica PURA de la ficha de crédito: derivados del plan de pagos, presentación de estados y la
// bitácora. Sin React y sin fetch — es lo que se prueba con vitest y lo que reusa cada pestaña.
import { ContractTypeEnum } from '@fiado/type-kit/bin/loanCredit/enums/ContractTypeEnum';
import { LoanDeviceLockReasonEnum } from '@fiado/type-kit/bin/loanCredit/enums/LoanDeviceLockReasonEnum';
import { LoanDeviceMdmStatusEnum } from '@fiado/type-kit/bin/loanCredit/enums/LoanDeviceMdmStatusEnum';
import { LoanCreditStatusEnum } from '@fiado/type-kit/bin/loanCredit/enums/LoanCreditStatusEnum';
import { LoanInstallmentStatusEnum } from '@fiado/type-kit/bin/loanCredit/enums/LoanInstallmentStatusEnum';
import { ClientLevelEnum } from '@fiado/type-kit/bin/loanScoring/enums/ClientLevelEnum';
import { MdmOperationTypeEnum } from '@fiado/type-kit/bin/mdm/enums/MdmOperationTypeEnum';
import type { LoanContractResponse } from '@fiado/type-kit/bin/loanCredit/dtos/responses/LoanContractResponse';
import type { LoanInstallmentResponse } from '@fiado/type-kit/bin/loanCredit/dtos/responses/LoanInstallmentResponse';
import { wireEnum } from '@/lib/wireEnum';
import { wireLookup } from '@/lib/wireLookup';
import type { LoanDeviceFleetItem } from '@/api/services/loan-credit';
import { formatDateMx, formatDateTimeMx, mxDayStartUtc } from '@/lib/dateHelpers';
import { formatMoneyCents } from '@/shared/lib/format/money';
import { arrearsDaysOf, isClearedInstallment, paidInstallmentsCountOf } from '@/lib/installments';
import { MdmLockModeEnum } from '@fiado/type-kit/bin/retailCatalog/enums/MdmLockModeEnum';
import {
    CUTOFF_PASSED_NOTE,
    CUTOFF_REFUTED_NOTE,
    getStatusPresentation,
    lockPhaseLabel,
    MDM_LOCK_PHASE_SHORT_LABEL,
    type MdmLockPhase,
} from '../mdm/logic';
import { lockPhaseOf, type LockReading } from '../lockPhase';
import type { MdmStatusTone } from '../mdm/types';

/** Lo que se pinta donde no hay dato. Nunca un cero: un ausente y un cero no son lo mismo. */
export const NO_VALUE = '—';

// Vive en shared; se re-exporta para los importadores del dominio.
export { formatMoneyCents };

/** Un importe ausente se dice ausente; solo un cero real se pinta como cero. */
export function formatOptionalMoneyCents(cents: number | undefined | null): string {
    return cents == null ? NO_VALUE : formatMoneyCents(cents);
}

/** La TNA viaja decimal (`2.30` = 230%). A peso entero y con el sufijo del mock. Sin tasa, ausente. */
export function formatTnaPct(tna: number | undefined | null): string {
    return tna == null ? NO_VALUE : `${(tna * 100).toFixed(0)}% (TNA)`;
}

/** Motivo del bloqueo en corto, para la sub-línea del widget MDM. */
export const LOCK_REASON_LABEL: Record<LoanDeviceLockReasonEnum, string> = {
    [LoanDeviceLockReasonEnum.OVERDUE_T_PLUS_1]: 'cuota vencida',
    [LoanDeviceLockReasonEnum.FRAUD]: 'fraude',
    [LoanDeviceLockReasonEnum.ADMIN_MANUAL]: 'bloqueo manual de un administrador',
};

/** La sub-línea del widget «Estado MDM». Solo el candado confirmado dice «bloqueado desde». */
export function mdmLockSubline(
    status: `${LoanDeviceMdmStatusEnum}`,
    lockReason: `${LoanDeviceLockReasonEnum}` | undefined,
    lockedOnLabel: string | undefined,
    reading: LockReading | null,
): string {
    const current = wireEnum<LoanDeviceMdmStatusEnum>(status);
    if (current !== LoanDeviceMdmStatusEnum.LOCKED) {
        if (reading === 'activeUnconfirmed') return 'orden dada, esperando al proveedor';
        if (reading === 'cutoffPassed')
            return 'pasó la hora del corte y el proveedor no confirma si cayó';
        if (reading === 'cutoffRefuted')
            return 'el corte debía caer y el proveedor dice que sigue sin candado';
        return current === LoanDeviceMdmStatusEnum.ACTIVE
            ? 'sin restricciones'
            : 'sin bloqueo activo';
    }
    if (reading === 'refuted') return 'el proveedor desmintió el bloqueo';
    const headline =
        reading === 'unconfirmed'
            ? 'orden de bloqueo esperando al proveedor'
            : reading === 'scheduled'
              ? lockedOnLabel
                  ? `bloqueo programado para ${lockedOnLabel}`
                  : 'bloqueo programado'
              : lockedOnLabel
                ? `bloqueado desde ${lockedOnLabel}`
                : 'bloqueado';
    const reason = lockReason ? [wireLookup(LOCK_REASON_LABEL, lockReason) ?? lockReason] : [];
    return [headline, ...reason].join(' · ');
}

/**
 * Lo que se sabe del candado. `CreditDeviceBlock` no trae la evidencia del proveedor, así que ahí
 * los dos campos llegan ausentes y el corte instalado decide solo, que es lo que ese wire sostiene.
 */
type LockScheduleInput = { lockedAt?: string; currentLockScheduledFor?: string } & Partial<
    Pick<LoanDeviceFleetItem, 'mdmConfirmed' | 'lockRefutedByProvider'>
>;

/** El instante solo si se puede leer y ya pasó: en «bloqueado desde» nunca va una hora futura. */
function pastInstantOf(instant: string | undefined, nowMs: number): string | undefined {
    if (instant === undefined) return undefined;
    const parsed = Date.parse(instant);
    return !Number.isNaN(parsed) && parsed <= nowMs ? instant : undefined;
}

/**
 * Si el candado todavía no cae y desde cuándo está puesto: la hora del corte cuando la hay —tanto
 * el que espera como el que ya cayó—, la de la orden cuando el proveedor no instala corte.
 */
export function lockScheduleOf(
    device: LockScheduleInput,
    nowMs: number = Date.now(),
): { at: string | undefined; scheduled: boolean } {
    const scheduledFor = device.currentLockScheduledFor;
    const fallsAt = scheduledFor === undefined ? Number.NaN : Date.parse(scheduledFor);
    const awaitsCut = !Number.isNaN(fallsAt) && fallsAt > nowMs;
    // El proveedor ya habló: confirmó el candado o lo desmintió, y ningún corte futuro lo discute.
    const settledByProvider = device.mdmConfirmed === true || device.lockRefutedByProvider === true;
    if (awaitsCut && !settledByProvider) return { at: scheduledFor, scheduled: true };
    const fellAt = Number.isNaN(fallsAt) || awaitsCut ? device.lockedAt : scheduledFor;
    return { at: pastInstantOf(fellAt, nowMs), scheduled: false };
}

/** Cómo se pinta el candado del equipo y la franja que lo matiza, si hace falta. */
export interface MdmLockHealth {
    label: string;
    tone: MdmStatusTone;
    /** El icono del candado cerrado; abierto en cualquier otro caso. */
    closedLock: boolean;
    warning: { tone: 'danger' | 'warning'; headline: string; body: string } | null;
}

type MdmLockHealthInput = Pick<
    LoanDeviceFleetItem,
    'mdmStatus' | 'lockedAt' | 'currentLockScheduledFor'
> &
    Partial<
        Pick<
            LoanDeviceFleetItem,
            'mdmConfirmed' | 'lockRefutedByProvider' | 'mdmAlerts' | 'unlockedAt' | 'mdmConfirmedAt'
        >
    >;

/** Solo la alerta LOCK_RETRY_EXHAUSTED sostiene que se reintentó hasta agotar los intentos. */
function refutedBodyOf(device: MdmLockHealthInput): string {
    const exhausted = (device.mdmAlerts ?? []).some(
        (alert) => alert.code === 'LOCK_RETRY_EXHAUSTED',
    );
    return exhausted
        ? 'Se reintentó hasta agotar los intentos y el equipo sigue sin candado: alguien tiene que resolverlo a mano.'
        : 'El equipo sigue sin candado: alguien tiene que revisarlo.';
}

/** El estado del candado, con la fase de `readLockPhase`: desmentido o sin confirmar nunca se ve sano. */
export function mdmLockHealthOf(
    device: MdmLockHealthInput,
    nowMs: number = Date.now(),
): MdmLockHealth {
    const isLocked =
        wireEnum<LoanDeviceMdmStatusEnum>(device.mdmStatus) === LoanDeviceMdmStatusEnum.LOCKED;
    const phase = lockPhaseOf(device, new Date(nowMs));
    if (phase === 'cutoffRefuted') {
        return {
            label: MDM_LOCK_PHASE_SHORT_LABEL.cutoffRefuted,
            tone: 'danger',
            closedLock: false,
            warning: {
                tone: 'danger',
                headline: 'El proveedor desmintió el corte.',
                body: CUTOFF_REFUTED_NOTE,
            },
        };
    }
    if (isLocked && device.lockRefutedByProvider === true) {
        return {
            label: MDM_LOCK_PHASE_SHORT_LABEL.refuted,
            tone: 'danger',
            closedLock: false,
            warning: {
                tone: 'danger',
                headline: 'El proveedor desmintió el bloqueo.',
                body: refutedBodyOf(device),
            },
        };
    }
    if (phase === 'scheduled') {
        return {
            label: MDM_LOCK_PHASE_SHORT_LABEL.scheduled,
            tone: 'warning',
            closedLock: false,
            warning: null,
        };
    }
    if (phase === 'cutoffPassed') {
        return {
            label: lockPhaseLabel(phase, undefined),
            tone: 'warning',
            closedLock: false,
            warning: {
                tone: 'warning',
                headline: 'El proveedor no confirmó este estado.',
                body: CUTOFF_PASSED_NOTE,
            },
        };
    }
    const base = getStatusPresentation(device.mdmStatus);
    if (phase === 'unconfirmed' || (!isLocked && device.mdmConfirmed === false)) {
        return {
            label: isLocked
                ? lockPhaseLabel('unconfirmed', undefined)
                : `${base.label} · sin confirmar`,
            tone: 'warning',
            closedLock: false,
            warning: {
                tone: 'warning',
                headline: 'El proveedor no confirmó este estado.',
                body: 'Lo inferimos nosotros, por ejemplo de un corte programado que ya venció: no lo des por verificado.',
            },
        };
    }
    return { label: base.label, tone: base.tone, closedLock: isLocked, warning: null };
}

/** El color del evento en la línea de tiempo, uno por tipo de operación (`.fk-mdm-event`). */
export const MDM_EVENT_TONE: Record<MdmOperationTypeEnum, CreditTone> = {
    [MdmOperationTypeEnum.LOCK]: 'danger',
    [MdmOperationTypeEnum.RESTRICT_APPS]: 'warning',
    [MdmOperationTypeEnum.AUTO_LOCK_SCHEDULE]: 'warning',
    [MdmOperationTypeEnum.NOTIFY]: 'warning',
    [MdmOperationTypeEnum.UNLOCK]: 'success',
    [MdmOperationTypeEnum.PIN_UNLOCK]: 'success',
    [MdmOperationTypeEnum.RELEASE]: 'success',
    [MdmOperationTypeEnum.STATUS]: 'info',
    [MdmOperationTypeEnum.LAST_SEEN]: 'info',
    [MdmOperationTypeEnum.ENROLL]: 'neutral',
    [MdmOperationTypeEnum.ARCHIVE]: 'neutral',
    [MdmOperationTypeEnum.DEACTIVATE]: 'neutral',
    [MdmOperationTypeEnum.LOG]: 'neutral',
};

/** Cuántos eventos muestra la línea de tiempo del diseño. */
export const MDM_TIMELINE_LIMIT = 5;

export interface MdmTimelineCut<T> {
    events: readonly T[];
    /** Cuántos quedaron fuera del corte; cero cuando se ven todos. */
    hidden: number;
}

/** Los eventos del equipo del más nuevo al más viejo, cortados al tope del diseño. */
export function recentMdmEvents<T extends { occurredAt: string }>(
    operations: readonly T[],
    limit: number = MDM_TIMELINE_LIMIT,
): MdmTimelineCut<T> {
    const ordered = [...operations].sort((a, b) => b.occurredAt.localeCompare(a.occurredAt));
    return { events: ordered.slice(0, limit), hidden: Math.max(ordered.length - limit, 0) };
}

/** El rótulo del historial. Cuando hubo corte lo dice: callarlo se lee como «esto es todo». */
export function mdmTimelineTally(cut: MdmTimelineCut<unknown>): string {
    const shown = cut.events.length;
    if (cut.hidden === 0) return `${String(shown)} ${shown === 1 ? 'evento' : 'eventos'}`;
    return `los ${String(shown)} más recientes de ${String(shown + cut.hidden)}`;
}

export type CreditTone = 'success' | 'danger' | 'warning' | 'info' | 'neutral';

export interface StatusPresentation {
    label: string;
    /** Forma corta para las celdas angostas de la tabla de cartera. */
    shortLabel: string;
    tone: CreditTone;
}

/** Los 7 estados del ciclo de vida del crédito (M2 §10), con el copy del mock. */
export const CREDIT_STATUS_PRESENTATION: Record<LoanCreditStatusEnum, StatusPresentation> = {
    [LoanCreditStatusEnum.PENDING_ACTIVATION]: {
        label: 'Pendiente de activación',
        shortLabel: 'Pend. act.',
        tone: 'warning',
    },
    [LoanCreditStatusEnum.ACTIVE]: { label: 'Activo', shortLabel: 'Activo', tone: 'success' },
    [LoanCreditStatusEnum.IN_ARREARS]: { label: 'En mora', shortLabel: 'Mora', tone: 'danger' },
    [LoanCreditStatusEnum.SETTLED]: { label: 'Liquidado', shortLabel: 'Liquid.', tone: 'info' },
    [LoanCreditStatusEnum.CANCELLED]: {
        label: 'Cancelado',
        shortLabel: 'Cancel.',
        tone: 'neutral',
    },
    [LoanCreditStatusEnum.DISCARDED]: {
        label: 'Descartado',
        shortLabel: 'Descart.',
        tone: 'neutral',
    },
    [LoanCreditStatusEnum.WRITTEN_OFF]: {
        label: 'Baja contable',
        shortLabel: 'Baja cont.',
        tone: 'neutral',
    },
};

/** Los 5 estados de cuota del contrato. */
export const INSTALLMENT_STATUS_PRESENTATION: Record<
    LoanInstallmentStatusEnum,
    StatusPresentation
> = {
    [LoanInstallmentStatusEnum.PENDING]: {
        label: 'Por vencer',
        shortLabel: 'Por vencer',
        tone: 'neutral',
    },
    [LoanInstallmentStatusEnum.PAID]: { label: 'Pagada', shortLabel: 'Pagada', tone: 'success' },
    [LoanInstallmentStatusEnum.PAID_IN_ADVANCE]: {
        label: 'Pagada por adelantado',
        shortLabel: 'Adelantada',
        tone: 'success',
    },
    [LoanInstallmentStatusEnum.OVERDUE]: {
        label: 'Vencida',
        shortLabel: 'Vencida',
        tone: 'danger',
    },
    [LoanInstallmentStatusEnum.RESTRUCTURED]: {
        label: 'Reestructurada',
        shortLabel: 'Reestr.',
        tone: 'info',
    },
};

/** Un valor del wire que el front no conoce: se muestra crudo y en tono neutro. */
function unknownPresentation(value: string): StatusPresentation {
    return { label: value, shortLabel: value, tone: 'neutral' };
}

export function creditStatusPresentationOf(status: string): StatusPresentation {
    return wireLookup(CREDIT_STATUS_PRESENTATION, status) ?? unknownPresentation(status);
}

export function installmentStatusPresentationOf(status: string): StatusPresentation {
    return wireLookup(INSTALLMENT_STATUS_PRESENTATION, status) ?? unknownPresentation(status);
}

/** Los 4 niveles del SCI, con el rótulo que pinta el mock. */
export const CLIENT_LEVEL_LABEL: Record<ClientLevelEnum, string> = {
    [ClientLevelEnum.GOLD]: 'Oro',
    [ClientLevelEnum.SILVER]: 'Plata',
    [ClientLevelEnum.BRONZE]: 'Bronce',
    [ClientLevelEnum.NOT_ELIGIBLE]: 'No elegible',
};

export function clientLevelLabelOf(level: string): string {
    return wireLookup(CLIENT_LEVEL_LABEL, level) ?? level;
}

export function progressPctOf(paid: number, total: number): number {
    return total > 0 ? Math.round((paid / total) * 100) : 0;
}

/**
 * En qué estado está una fila de la pestaña Docs. `unsigned` (el contrato existe sin PDF firmado)
 * y `not-issued` (nadie emitió el documento) son hechos DISTINTOS: aplastarlos esconde un pendiente.
 */
export type CreditDocumentState = 'available' | 'unsigned' | 'not-issued';

/** Una fila del expediente del crédito, ya resuelta contra los contratos del silo. */
export interface CreditDocumentRow {
    id: string;
    /** Rótulo corto del chip apagado: «Contrato», «Pagaré», «Tabla»… */
    chip: string;
    name: string;
    state: CreditDocumentState;
    /** Instante del contrato que respalda la fila. Ausente = la fila no pinta fecha. */
    createdAt?: string;
    /** Solo en `available`: es lo que pide el enlace de descarga del PDF. */
    contractId?: string;
}

/** Una fila del expediente antes de resolverla. Sin `type` = ningún endpoint la emite. */
interface CreditDocumentDefinition {
    id: string;
    chip: string;
    name: string;
    type?: ContractTypeEnum;
}

/** El expediente que el crédito muestra, en el orden del diseño. Tres filas no tienen fuente. */
const CREDIT_DOCUMENT_DEFINITIONS: readonly CreditDocumentDefinition[] = [
    { id: 'credit', chip: 'Contrato', name: 'Crédito Celular', type: ContractTypeEnum.CREDIT },
    {
        id: 'promissory-note',
        chip: 'Pagaré',
        name: 'Pagaré',
        type: ContractTypeEnum.PROMISSORY_NOTE,
    },
    { id: 'schedule', chip: 'Tabla', name: 'Tabla de amortización' },
    { id: 'legal-annex', chip: 'Anexo', name: 'Anexo de Disposiciones Legales' },
    { id: 'mdm-enrollment', chip: 'Enrol', name: 'Constancia enrolamiento MDM' },
    {
        id: 'sic-authorization',
        chip: 'SIC',
        name: 'Autorización de consulta a Círculo de Crédito',
        type: ContractTypeEnum.SIC_AUTHORIZATION,
    },
    {
        id: 'signature-evidence',
        chip: 'Firma',
        name: 'Constancia de firma electrónica',
        type: ContractTypeEnum.SIGNATURE_EVIDENCE,
    },
];

/**
 * Las siete filas SIEMPRE, en orden fijo: la que no tiene documento se ve faltando, no desaparece.
 * Un `type` que el silo mande y no esté en la lista se ignora — las filas no salen del dato.
 */
export function buildCreditDocumentRows(
    contracts: readonly LoanContractResponse[],
): readonly CreditDocumentRow[] {
    return CREDIT_DOCUMENT_DEFINITIONS.map(({ id, chip, name, type }) => {
        const contract = type ? contracts.find((item) => item.type === type) : undefined;
        if (!contract) return { id, chip, name, state: 'not-issued' as const };
        return {
            id,
            chip,
            name,
            state: contract.documentId ? ('available' as const) : ('unsigned' as const),
            createdAt: contract.createdAt,
            contractId: contract.documentId ? contract.contractId : undefined,
        };
    });
}

/** Las cinco variantes del timeline del mock; cada una tiene su color de punto. */
export type CreditHistoryKind =
    | 'creation'
    | 'activation'
    | 'arrears'
    | 'block'
    | 'scheduledBlock'
    | 'settlement';

export interface CreditHistoryEvent {
    id: string;
    kind: CreditHistoryKind;
    /** Instante ISO. Es la clave del orden y lo que pinta la fecha del renglón. */
    at: string;
    title: string;
    detail: string;
}

export interface CreditHistoryInput {
    createdAt: string;
    updatedAt: string;
    activatedAt?: string;
    status: LoanCreditStatusEnum;
    financedCapitalCents: number;
    termWeeks: number;
    imei?: string;
    installments: readonly LoanInstallmentResponse[];
    /** El equipo del crédito; `null` = no tiene enrolado, `undefined` = no se pudo leer. */
    device: LoanDeviceFleetItem | null | undefined;
    /** Día del piso de venta (`YYYY-MM-DD`) contra el que se cuentan los días de atraso. */
    today: string;
    /** Instante contra el que se decide si el candado ya entró; por defecto, ahora. */
    nowMs?: number;
}

/** El motivo del bloqueo en una palabra, como lo dice la sublínea del mock. */
const LOCK_REASON_WORD: Record<LoanDeviceLockReasonEnum, string> = {
    [LoanDeviceLockReasonEnum.OVERDUE_T_PLUS_1]: 'mora',
    [LoanDeviceLockReasonEnum.FRAUD]: 'fraude',
    [LoanDeviceLockReasonEnum.ADMIN_MANUAL]: 'bloqueo manual',
};

/** La cuota vencida más vieja: su vencimiento es el día en que el crédito entró en mora. */
function arrearsSinceOf(installments: readonly LoanInstallmentResponse[]): string | null {
    const due = installments
        .filter((item) => !isClearedInstallment(item.status) && item.dueDate)
        .map((item) => item.dueDate as string)
        .sort();
    return due[0] ?? null;
}

/** El último pago registrado en el plan: es cuando el crédito terminó de cubrirse. */
function lastPaidAtOf(installments: readonly LoanInstallmentResponse[]): string | null {
    const paid = installments
        .filter((item) => item.paidAt)
        .map((item) => item.paidAt as string)
        .sort();
    return paid.at(-1) ?? null;
}

/** El evento de un bloqueo cuya hora ya pasó: solo la fase `locked` afirma el candado. */
function pastLockEventOf(phase: MdmLockPhase | null): {
    kind: 'block' | 'scheduledBlock';
    title: string;
} {
    switch (phase) {
        case 'locked':
            return { kind: 'block', title: 'Dispositivo bloqueado por MDM' };
        case 'refuted':
            return { kind: 'block', title: 'Bloqueo desmentido por el proveedor' };
        case 'unconfirmed':
        case 'scheduled':
            return { kind: 'scheduledBlock', title: 'Orden de bloqueo sin confirmar' };
        case 'cutoffRefuted':
            return { kind: 'block', title: 'Corte desmentido por el proveedor' };
        case 'cutoffPassed':
        case null:
            return { kind: 'block', title: 'Orden de bloqueo por MDM' };
    }
}

/**
 * Bitácora derivada de fechas REALES, de la más nueva a la más vieja. Nada se inventa.
 * El pago aplicado que dibuja el mock no está: `loan-credit` no publica ledger de pagos.
 */
export function buildCreditHistory(input: CreditHistoryInput): readonly CreditHistoryEvent[] {
    const events: CreditHistoryEvent[] = [
        {
            id: 'origination',
            kind: 'creation',
            at: input.createdAt,
            title: 'Crédito originado',
            detail: `${formatMoneyCents(input.financedCapitalCents)} a ${String(input.termWeeks)} semanas`,
        },
    ];

    const imei = input.imei ?? input.device?.imei;

    // Activación y enrolamiento son EXCLUYENTES: mientras el crédito no se active, lo que hay que
    // contar es en qué punto va el enrolamiento del equipo.
    if (input.activatedAt) {
        events.push({
            id: 'activation',
            kind: 'activation',
            at: input.activatedAt,
            title: 'Crédito activado',
            detail: `Enganche pagado · MDM enrolado${imei ? ` · IMEI ${imei}` : ''}`,
        });
    } else {
        events.push({
            id: 'enrollment',
            kind: 'creation',
            at: input.createdAt,
            title: 'Enrolamiento MDM',
            detail: imei ? `IMEI ${imei}` : 'Enrolamiento en proceso',
        });
    }

    const arrearsDays = arrearsDaysOf(input.installments, input.today);
    const arrearsSince = arrearsSinceOf(input.installments);
    if (arrearsDays !== null && arrearsDays > 0 && arrearsSince) {
        events.push({
            id: 'arrears',
            kind: 'arrears',
            at: mxDayStartUtc(arrearsSince),
            title: 'Entró en mora',
            detail: `${String(arrearsDays)} días de atraso`,
        });
    }

    if (input.device?.lockedAt) {
        const reason = input.device.lockReason
            ? (wireLookup(LOCK_REASON_WORD, input.device.lockReason) ?? input.device.lockReason)
            : 'mora';
        const nowMs = input.nowMs ?? Date.now();
        const { scheduled, at } = lockScheduleOf(input.device, nowMs);
        const orderedAtMs = Date.parse(input.device.lockedAt);
        const orderStillAhead = !Number.isNaN(orderedAtMs) && orderedAtMs > nowMs;
        const awaitsLock = scheduled || orderStillAhead;
        const phase = lockPhaseOf(input.device, new Date(nowMs));
        const pastLock = awaitsLock ? null : pastLockEventOf(phase);
        events.push({
            id: 'device-lock',
            kind: pastLock?.kind ?? 'scheduledBlock',
            // El corte que espera y la orden cuya hora todavía no llega se fechan en la orden; el
            // candado que ya cayó, en la hora en que cayó, y si no se sabe vuelve a la de la orden.
            at: awaitsLock ? input.device.lockedAt : (at ?? input.device.lockedAt),
            title: pastLock?.title ?? 'Bloqueo programado por MDM',
            detail:
                scheduled && at
                    ? `Motivo: ${reason} · se aplica ${formatDateTimeMx(at)}`
                    : `Motivo: ${reason}`,
        });
    }

    // Sin ledger, la liquidación se fecha con el último pago del plan; si el plan no lo trae, con
    // la última escritura del crédito, que es cuando el silo lo marcó liquidado.
    if (input.status === LoanCreditStatusEnum.SETTLED) {
        events.push({
            id: 'settlement',
            kind: 'settlement',
            at: lastPaidAtOf(input.installments) ?? input.updatedAt,
            title: 'Crédito liquidado',
            detail: 'Todas las cuotas cubiertas',
        });
    }

    return events.sort((a, b) => b.at.localeCompare(a.at));
}

/** La alerta contextual que cierra «Estado a hoy»: habla del CRÉDITO, con el tono de su estado. */
export interface CreditAlert {
    tone: CreditTone;
    text: string;
}

export interface CreditAlertInput {
    status: LoanCreditStatusEnum;
    /** `null` = el plan no se pudo leer. Sin él no se afirma que el crédito esté al corriente. */
    overdueDays: number | null;
    /** `null` = el expediente MDM no se pudo leer, que no es «el equipo está libre». */
    deviceLocked: boolean | null;
    /** El instante del que habla la alerta: la hora del corte si está programado, la de la orden si no. */
    lockedAt?: string;
    /** La lectura del candado: solo `locked` permite decir «Dispositivo bloqueado». */
    lockReading: LockReading | null;
    lockReason?: `${LoanDeviceLockReasonEnum}`;
    /** El modo del equipo: solo el bajo demanda lo bloquea el barrido T+1. Ausente = no se sabe. */
    lockMode?: `${MdmLockModeEnum}`;
}

/** Lo que se dice de la mora según el modo; sin modo no se afirma cuándo se bloquea. */
function overdueTextOf(overdueDays: number, lockMode: `${MdmLockModeEnum}` | undefined): string {
    const days = `${String(overdueDays)} días de mora.`;
    const mode = lockMode === undefined ? undefined : wireEnum<MdmLockModeEnum>(lockMode);
    if (mode === MdmLockModeEnum.ONDEMAND)
        return `${days} Bloqueo T+1 automático si cupón vencido sigue sin pago.`;
    if (mode === MdmLockModeEnum.SCHEDULED)
        return `${days} El equipo se bloquea en su corte programado, no al día siguiente del vencimiento.`;
    return days;
}

/** Lo que hay que decir de ESTE crédito hoy, en las cinco líneas que dibuja el mock. */
export function creditAlertOf({
    status,
    overdueDays,
    deviceLocked,
    lockedAt,
    lockReading,
    lockReason,
    lockMode,
}: CreditAlertInput): CreditAlert {
    if (deviceLocked === true) {
        const since = lockedAt ? formatDateMx(lockedAt) : NO_VALUE;
        const reason = lockReason
            ? (wireLookup(LOCK_REASON_WORD, lockReason) ?? lockReason)
            : 'mora';
        if (lockReading === 'refuted') {
            return {
                tone: 'danger',
                text: 'El proveedor desmintió el bloqueo: el equipo sigue sin candado.',
            };
        }
        if (lockReading === 'unconfirmed') {
            return {
                tone: 'warning',
                text: `Orden de bloqueo del ${since} esperando al proveedor: no está confirmada. Motivo: ${reason}.`,
            };
        }
        if (lockReading === 'scheduled' && lockedAt) {
            return {
                tone: 'warning',
                text: `Bloqueo del dispositivo programado para ${formatDateTimeMx(lockedAt)}. Motivo: ${reason}.`,
            };
        }
        return {
            tone: 'danger',
            text: `Dispositivo bloqueado desde ${since}. Motivo: ${reason}.`,
        };
    }
    if (lockReading === 'cutoffPassed') {
        return { tone: 'warning', text: CUTOFF_PASSED_NOTE };
    }
    if (lockReading === 'cutoffRefuted') {
        return { tone: 'danger', text: CUTOFF_REFUTED_NOTE };
    }
    if (overdueDays !== null && overdueDays > 0) {
        return { tone: 'warning', text: overdueTextOf(overdueDays, lockMode) };
    }
    if (status === LoanCreditStatusEnum.PENDING_ACTIVATION) {
        return {
            tone: 'warning',
            text: 'Pendiente de activar. Caduca a 30 días de la solicitud.',
        };
    }
    if (status === LoanCreditStatusEnum.SETTLED) {
        return {
            tone: 'info',
            text: 'Crédito liquidado. Cliente elegible para renovación.',
        };
    }
    return { tone: 'success', text: 'Crédito al corriente. Sin alertas activas.' };
}

/** Cuántas cuotas hay de cada estado y cuánto se debe de las vencidas. */
export interface ScheduleTally {
    total: number;
    paid: number;
    overdue: number;
    pending: number;
    restructured: number;
    /** Lo que se debe de las cuotas vencidas, en cents. */
    overdueDueCents: number;
}

export function tallySchedule(installments: readonly LoanInstallmentResponse[]): ScheduleTally {
    const countOf = (status: LoanInstallmentStatusEnum) =>
        installments.filter((item) => item.status === status).length;
    const overdue = installments.filter(
        (item) => item.status === LoanInstallmentStatusEnum.OVERDUE,
    );
    return {
        total: installments.length,
        paid: paidInstallmentsCountOf(installments),
        overdue: overdue.length,
        pending: countOf(LoanInstallmentStatusEnum.PENDING),
        restructured: countOf(LoanInstallmentStatusEnum.RESTRUCTURED),
        overdueDueCents: overdue.reduce((total, item) => total + item.totalCents, 0),
    };
}

/** «Desglose · 3 de 8 cuotas pagadas · 1 vencida» — el título de la card, como el mock. */
export function scheduleHeadlineOf(tally: ScheduleTally): string {
    const head = `Desglose · ${String(tally.paid)} de ${String(tally.total)} cuotas pagadas`;
    if (tally.overdue === 0) return head;
    return `${head} · ${String(tally.overdue)} ${tally.overdue === 1 ? 'vencida' : 'vencidas'}`;
}

/**
 * El detalle de la derecha. Los tres conteos del mock van SIEMPRE, cero incluido; reestructuradas y
 * el monto en mora solo cuando existen.
 */
export function scheduleTallyLineOf(tally: ScheduleTally): string {
    const parts = [
        `${String(tally.paid)} ${tally.paid === 1 ? 'pagada' : 'pagadas'}`,
        `${String(tally.overdue)} ${tally.overdue === 1 ? 'vencida' : 'vencidas'}`,
        `${String(tally.pending)} por vencer`,
        ...(tally.restructured > 0
            ? [
                  `${String(tally.restructured)} ${tally.restructured === 1 ? 'reestructurada' : 'reestructuradas'}`,
              ]
            : []),
        ...(tally.overdueDueCents > 0
            ? [`${formatMoneyCents(tally.overdueDueCents)} en mora`]
            : []),
    ];
    return parts.join(' · ');
}
